import React, { useState, useEffect } from 'react';
import { get, clone, flatten, cloneDeep, last } from 'lodash';
import { connect } from 'react-redux';
import { withTranslation } from 'react-i18next';
import styled from 'styled-components';
import CModal from '_Components/CModal/CModal';
import { QSideBarLayout } from '_Components/Layouts/QSideBarLayout';
import QButton from '_Components/Buttons/QButton/QButton';
import {
  StyledDropdown,
  StyledDropdownToggle,
  Item,
  ToggleButton,
  Menu,
  Label,
} from '_Cards/FilterCards/DataByFiltersCard/_Containers/Dropdowns/utils/DropdownStyles';
import { ImageSize } from 'styles/abstracts/variables';
import Icon from '_Components/Icons/Icon';
import { setLevelToDisplay } from '_Resources/Benchmark/Actions/setLevelToDisplay';
import { setOnlySelectedCategories } from '_Resources/Benchmark/Actions/setOnlySelectedCategories';
import { selectThematicSelection } from '_Resources/Cards/Actions/selectThematicSelection';
import { useQuery, useMutation } from 'react-apollo';
import GET_THEMATICS_OVERVIEW_DATA_QUERY from '_Cards/ThematicsCard/ThematicsOverviewCards/graphql/getThematicsOverviewData';
import ThematicSelectionDropdown from '_Cards/ThematicsCard/ThematicsOverviewCards/CategoryBreakdownCard/_Containers/DropdownsContainer/_Components/ThematicSelectionDropdown';
import {
  getSelectedThematicsForSettings,
  getDefaultSettings,
} from '_Cards/ThematicsCard/ThematicsOverviewCards/CategoryBreakdownCard/utils';
import SAVE_THEMATICS_OVERVIEW_SETTINGS from '_Cards/ThematicsCard/ThematicsOverviewCards/graphql/saveThematicsOverviewSettings';
import GET_THEMATIC_SELECTION_LIST from '_Cards/ThematicsCard/ThematicsOverviewCards/graphql/getThematicSelectionList.js';
import { setThematicSelectionList } from '_Resources/Cards/Actions/setThematicSelectionList';

const StyledCModal = styled(CModal)`
  max-width: 500px !important;
`;

const HeaderArea = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Body = styled.div`
  margin: -16px;
`;

const QSideBarLayoutDisplayArea = styled.div`
  flex-grow: 1;
  /* overflow: hidden; */
`;

const StyledQButton = styled(QButton)`
  display: flex;
  align-items: center;
  border-radius: 50%;
  justify-content: center;
  position: absolute;
  right: 1em;
  top: 1em;
  padding: 0 !important;
`;

const SettingDiv = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin: 2em;
  font-weight: bold;
`;

const ModalFooter = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const StyledToggleButton = styled(ToggleButton)`
  width: 13rem;
`;

type BenchmarkSettingsModalProps = {
  onClose: any;
  isOpen: boolean;
  t: any;
  dispatchSetLevelToDisplay: any;
  dispatchSetOnlySelectedCategories: any;
  benchmarkLevelToDisplay: string;
  dispatchSelectThematicSelection: Function;
  dispatchSetThematicSelectionList: Function;
  projectId: number;
  filterKeys: any;
  selectionList: any[];
  selectedThematicSelectionOption: any;
  range: {
    startDate: Date;
    endDate: Date;
  };
  dataCollectorId: number;
  thematicSelectionId: number;
};

const BenchmarkSettingsModal = ({
  onClose,
  isOpen,
  t,
  dispatchSetLevelToDisplay,
  dispatchSetOnlySelectedCategories,
  benchmarkLevelToDisplay,
  dispatchSelectThematicSelection,
  dispatchSetThematicSelectionList,
  projectId,
  filterKeys,
  selectionList,
  selectedThematicSelectionOption,
  range,
  dataCollectorId,
  thematicSelectionId,
}: BenchmarkSettingsModalProps) => {
  const [isDropdownOpen, openDropdown] = useState<boolean>(false);
  const [settings, setSettings] = useState({});
  const [queryFilters, setQueryFilters] = useState(filterKeys);

  useEffect(() => {
    const filters = cloneDeep(filterKeys);
    filters.keywords = flatten(filterKeys.texts?.filter(t => t.name === 'keywords').map(t => t.values)) || [];
    delete filters.texts;
    setQueryFilters(filters);
  }, [filterKeys]);

  const { data: thematicsOverviewData, loading, refetch } = useQuery(GET_THEMATICS_OVERVIEW_DATA_QUERY, {
    variables: { ...queryFilters, projectId, range, dataCollectorId, thematicSelectionId },
  });

  let formattedData: any[] = [];

  const formatDeep = (arrayToIterate, level, objToFill) => {
    let newObjToFill = {};
    arrayToIterate?.map(item => {
      const childrenKey = Object.keys(item).find(i => Array.isArray(item[i]));
      newObjToFill = {
        ...objToFill,
        [level]: item.thematicsOverviewDetails.name,
      };
      formattedData = [...formattedData, newObjToFill];
      if (Array.isArray(item[childrenKey])) {
        formatDeep(
          item[childrenKey],
          childrenKey?.toLowerCase()?.substring(0, childrenKey.length - 1),
          newObjToFill,
          formattedData,
        );
      }
    });
    return formattedData;
  };

  const categoriesHierarchies = formatDeep(
    thematicsOverviewData?.getThematicsOverviewData?.thematics,
    'thematic',
    {},
  ).map(item => {
    if (!item.subthematic) {
      return { ...item, subthematic: null, subsubthematic: null };
    }
    if (!item.subsubthematic) {
      return { ...item, subsubthematic: null };
    }
    return item;
  });

  const defaultSettings = getDefaultSettings(categoriesHierarchies);

  useEffect(() => {
    if (thematicsOverviewData) {
      setSettings(
        JSON.parse(thematicsOverviewData.getThematicsOverviewData.settings)
          ? {
              ...defaultSettings,
              ...JSON.parse(thematicsOverviewData.getThematicsOverviewData.settings),
              selectedThematics:
                JSON.parse(thematicsOverviewData.getThematicsOverviewData.settings).selectedThematics || formattedData,
            }
          : defaultSettings,
      );
    }
  }, [thematicsOverviewData, loading]);

  const buttonRight = (
    <StyledQButton
      bgColor="greyShades.g300"
      type="button"
      onClick={() => onClose()}
      size="small"
      icon="CROSS"
      iconSize={24}
    />
  );

  const thematicsToDisplay = [
    {
      id: 'thematics',
      label: 'Categories',
    },
    {
      id: 'subthematics',
      label: 'Subcategories',
    },
    {
      id: 'subsubthematics',
      label: 'Sub sub categories',
    },
  ];

  const [currentItem, setCurrentItem] = useState(
    thematicsToDisplay.find(opt => opt.id === benchmarkLevelToDisplay) || thematicsToDisplay[1],
  );

  const onValidate = () => {
    dispatchSetLevelToDisplay(currentItem.id);
    const onlySelectedCategoriesForBenchmarkCategoryBreakdown = selectedThematicSelectionOption?.id !== null;
    dispatchSetOnlySelectedCategories(onlySelectedCategoriesForBenchmarkCategoryBreakdown);
  };

  const getFooter = () => {
    return (
      <ModalFooter>
        <QButton onClick={() => onClose()} size="big" bgColor="gray">
          {t('Cancel')}
        </QButton>
        <QButton
          onClick={() => {
            onValidate();
            onClose();
          }}
          size="big"
          bgColor="purple"
        >
          {t('Validate')}
        </QButton>
      </ModalFooter>
    );
  };

  const allThematics = {
    createdBy: {
      firstName: 'system',
      lastName: 'system',
      email: 'support@qemotion.com',
    },
    id: null,
    label: t('button:All thematics'),
    name: t('button:All thematics'),
    projectId,
    thematics: [],
  };

  const { data: dataThematicSelectionList, loading: loadingSelectionList, refetch: refetchSelectionList } = useQuery(
    GET_THEMATIC_SELECTION_LIST,
    {
      variables: { projectId },
    },
  );

  const [saveThematicsOverviewSettings] = useMutation(SAVE_THEMATICS_OVERVIEW_SETTINGS, {
    variables: {
      projectId,
      thematicSelectionId: selectedThematicSelectionOption?.id || null,
      settings: JSON.stringify(settings),
    },
    update: () => {},
  });

  useEffect(() => {
    if (dataThematicSelectionList) {
      const optionsList = dataThematicSelectionList.getThematicSelectionList.map(selection => ({
        ...selection,
        label: selection.name,
      }));

      dispatchSetThematicSelectionList(optionsList);

      const foundStoredThematicSelectionId = optionsList
        .map(option => option.id)
        .find(id => id === selectedThematicSelectionOption?.id);

      if (!foundStoredThematicSelectionId) {
        dispatchSelectThematicSelection(last(optionsList));
        const cloneSettings = clone(settings);
        const thematicsVariableForSettings = getSelectedThematicsForSettings(last(optionsList)?.thematics);
        cloneSettings.selectedThematics = thematicsVariableForSettings || [];
        saveThematicsOverviewSettings({
          variables: {
            projectId,
            settings: JSON.stringify(cloneSettings),
            thematicSelectionId: last(optionsList)?.id || null,
          },
          refetchQueries: [
            {
              query: GET_THEMATICS_OVERVIEW_DATA_QUERY,
              variables: { ...filterKeys, projectId, range, dataCollectorId, thematicSelectionId },
            },
          ],
        });
      }
    }
  }, [dataThematicSelectionList]);

  const onThematicSelectionChange = selectThematicSelectionOption => {
    dispatchSelectThematicSelection(selectThematicSelectionOption);
    const cloneSettings = clone(settings);
    cloneSettings.selectedThematics = getSelectedThematicsForSettings(selectThematicSelectionOption.thematics);
    if (selectThematicSelectionOption.id !== null) {
      cloneSettings.categoryBreakdown.onlySelectedCategories = true;
    }
    if (selectThematicSelectionOption.id === null) {
      cloneSettings.selectedThematics = [];
      cloneSettings.categoryBreakdown.onlySelectedCategories = false;
    }
    saveThematicsOverviewSettings({
      variables: {
        projectId,
        settings: JSON.stringify(cloneSettings),
        thematicSelectionId: selectThematicSelectionOption?.id || null,
      },
      refetchQueries: [
        {
          query: GET_THEMATICS_OVERVIEW_DATA_QUERY,
          variables: {
            ...filterKeys,
            projectId,
            range,
            dataCollectorId,
            thematicSelectionId: selectThematicSelectionOption?.id || null,
          },
        },
      ],
    });
  };

  return (
    <>
      <StyledCModal
        data={{}}
        title={t('Settings')}
        displayMentionsSubtitle={false}
        displayCardSubtitle={false}
        displayDateSubtitle={false}
        modal={isOpen}
        footer={getFooter()}
        toggle={onClose}
        buttonRight={buttonRight}
        centered
      >
        <Body>
          <HeaderArea />
          <QSideBarLayout>
            <QSideBarLayoutDisplayArea>
              <SettingDiv>
                <Label>{t('Level to display')}</Label>
                <StyledDropdown direction="down" isOpen={isDropdownOpen} toggle={() => openDropdown(!isDropdownOpen)}>
                  <StyledDropdownToggle>
                    <StyledToggleButton disableHover bgColor="grayShades.g300">
                      {t(currentItem.label)}
                      <Icon icon="INTERFACE_CHEVRON_DOWN" size={ImageSize.medium} />
                    </StyledToggleButton>
                  </StyledDropdownToggle>
                  <Menu className="dataByFiltersDropdownMenu">
                    {thematicsToDisplay.map(option => {
                      return (
                        <Item
                          disabled={currentItem.id === option.id}
                          onClick={() => {
                            setCurrentItem(option);
                          }}
                        >
                          <span>{t(option.label)}</span>
                        </Item>
                      );
                    })}
                  </Menu>
                </StyledDropdown>
              </SettingDiv>
              <SettingDiv>
                <ThematicSelectionDropdown
                  id="thematicSelectionDropdownContainer"
                  label="button:Selected categories"
                  benchmark
                  t={t}
                  options={[allThematics, ...selectionList]}
                  thematicSelection={selectedThematicSelectionOption}
                  setThematicSelection={onThematicSelectionChange}
                />
              </SettingDiv>
            </QSideBarLayoutDisplayArea>
          </QSideBarLayout>
        </Body>
      </StyledCModal>
    </>
  );
};

const mapStateToProps = (state: any) => ({
  benchmarkLevelToDisplay: get(state, ['benchmark', 'levelToDisplay']),
  projectId: get(state, ['projects', 'currentProject', 'id']),
  filterKeys: get(state, ['filters', 'filterKeys']),
  selectionList: get(state, ['thematicSelections', 'list']),
  selectedThematicSelectionOption: get(state, ['thematicSelections', 'selectedThematicSelectionOption']),
  range: get(state, ['filters', 'filterKeys', 'range']),
  dataCollectorId: get(state, ['filters', 'filterKeys', 'dataCollectorId']),
  thematicSelectionId: get(state, ['thematicSelections', 'selectedThematicSelectionOption', 'id']),
});

const mapDispatchToProps = (dispatch: any) => ({
  dispatchSetLevelToDisplay: (newState: any) => {
    dispatch(setLevelToDisplay(newState));
  },
  dispatchSetOnlySelectedCategories: (newState: any) => {
    dispatch(setOnlySelectedCategories(newState));
  },
  dispatchSelectThematicSelection: (thematicSelection: any) => {
    dispatch(selectThematicSelection(thematicSelection));
  },
  dispatchSetThematicSelectionList: thematicSelectionList => {
    dispatch(setThematicSelectionList(thematicSelectionList));
  },
});

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(withTranslation(['button', 'benchmark'])(BenchmarkSettingsModal));
